// Copyright (c) Docugami, Inc. All rights reserved.

import { AppState } from '.';
import { ClusterInfo, ClusterDocument, BuildState } from '../networking/api';
import { DocumentState } from './documents/types';
import { ClustersState, ClusterState } from './clusters/types';
import { UnassignedDocSetId } from 'components/file_management/file_helpers';
import { Dispatch } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { selectGetOrganizationSamplesResponseQuery } from './clusters/samplesSlice';
import { SampleStatus } from './clusters/samplesApi';
import { BuildType } from "networking/BuildType";
import { PipelineBuildState } from "store/organization/types";

export function getClusterById(state: AppState, clusterId: string): ClusterState {
    return state.clusters.indexed[clusterId];
}

export function getClusterInfoById(state: AppState, clusterId: string): ClusterInfo {
    const clusterState = getClusterById(state, clusterId);
    // @ts-ignore
    return !!clusterState ? clusterState.clusterInfo : null;
}

export function getClusterNameById(clusterId: string) {
    return function (dispatch: Dispatch<any>, getState: (() => AppState)): string {
        const state = getState();
        const clusterInfo = getClusterInfoById(state, clusterId);
        return clusterInfo?.label ?? '';
    }
}

export function getDocumentOrder(state: AppState, clusterId: string): string[] {
    const cluster = getClusterById(state, clusterId);
    return !!cluster ? cluster.docIds : [];
}

export function getClusterIdForDocument(state: AppState, documentId: string) {
    const document = getDocumentById(state, documentId);
    if (!document || !document.clusterDocument.clusterId) {
        return '';
    }

    return document.clusterDocument.clusterId;
}

export function getDocumentById(state: AppState, documentId: string): DocumentState {
    return state.documents.indexed[documentId];
}

export function getDocumentNamesById(state: AppState, documentIds: string[]): string[] {
    return documentIds.map(id => getDocumentById(state, id)?.clusterDocument.filename ?? null).filter(x => !!x);
}

export function getClusterDocumentByFileId(state: AppState, docSetId: string, fileId: string): ClusterDocument | undefined {
    const docSet = getClusterById(state, docSetId);
    if (!!docSet) {
        for (const id of docSet.docIds) {
            const docState = state.documents.indexed[id];
            if (docState?.clusterDocument.fileId === fileId) {
                return docState.clusterDocument;
            }
        }
    }

    return undefined;
}

export function getNumberOfDocumentsInCurCluster(state: AppState, clusterId: string): number {
    const curClusterInfo = getClusterInfoById(state, clusterId);
    return !!curClusterInfo ? curClusterInfo.numberOfDocuments : 0;
}

export function mapCurClusterDocumentsFetched(state: AppState, clusterId: string): boolean {
    const cluster = getClusterById(state, clusterId);
    if (!!cluster) {
        return cluster.documentsFetchState?.fetched;
    }

    return false;
}

export function mapCurrentSelectedPage(state: AppState): number {
    return state.sdv.document?.currentSelectedPage ?? 0;
}

export function mapCurrentPageOnScreen(state: AppState): number {
    return state.sdv.document?.currentPageOnScreen ?? 0;
}

export function mapVisibleUserClusters(state: ClustersState): ClusterInfo[] {
    return state.allIds
        .map(id => state.indexed[id].clusterInfo)
        .filter(info => !!info && !info.isDocugamiCluster && info.clusterId !== UnassignedDocSetId)
        .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''))
}

export function getIsDocSetInstalling(state: AppState, docSetId: string) {
    const orgSamples = selectGetOrganizationSamplesResponseQuery(state).data;
    return orgSamples?.entities[docSetId]?.status === SampleStatus.Installing;
}

export const createGetDocSetDocuments = () => createSelector(
    [
        (state: AppState, docSetId: string) => state.clusters.indexed[docSetId]?.docIds,
        (state: AppState, docSetId: string) => state.documents.indexed,
    ],
    (docIds, documents) => docIds?.map(docId => documents[docId]?.clusterDocument).filter(x => !!x) ?? []
)

export const isBuildActive = (buildType: BuildType, builds: PipelineBuildState[], docSetId?: string) => {
    return builds.findIndex(x =>
        x.buildRequest.buildType === buildType &&
        (!docSetId || docSetId === x.buildRequest.clusterId) &&
        activeBuildFilter(x)) !== -1;
}

const activeBuildFilter = (build: PipelineBuildState): boolean => build.state === BuildState.NotStarted || build.state === BuildState.InProgress;
